/**
 * Fiches telles que l'appareil les garde (copie synchronisée), avec leur
 * contexte : la journée de l'accueil, le planning, la fiche intervention et
 * la fiche machine hors ligne lisent la même chose (WEB1-05, WEB1-08).
 * Chaque ligne passe par `readEntity` : un geste encore en file (étape,
 * modification) s'y voit déjà.
 */
import { listEntities, readEntity } from './sync.js';

/** Ligne d'intervention livrée par `/sync/pull`. */
export interface CachedInterventionRow {
  id: string;
  type: string;
  priority: string;
  status: string;
  starts_at: string | null;
  ends_at: string | null;
  time_approx_minutes?: number | null;
  assignee_id: string | null;
  assignee_name?: string | null;
  customer_id: string;
  site_id: string;
  equipment_id?: string | null;
  updated_at?: string;
  version?: number;
  [field: string]: unknown;
}

export interface CachedCustomer {
  display_name?: string;
  phone?: string | null;
}

export interface CachedSite {
  label?: string;
  address?: string;
  latitude?: number | null;
  longitude?: number | null;
  local_contact_name?: string | null;
  local_contact_phone?: string | null;
  sector?: string | null;
}

export interface CachedEquipment {
  category?: string;
  brand?: string | null;
  model?: string | null;
  serial_number?: string | null;
}

export interface CachedVisit {
  intervention: CachedInterventionRow;
  customer?: CachedCustomer | undefined;
  site?: CachedSite | undefined;
  equipment?: CachedEquipment | undefined;
}

async function context(row: CachedInterventionRow): Promise<CachedVisit> {
  const customer: CachedCustomer | undefined = await readEntity(row.customer_id);
  const site: CachedSite | undefined = await readEntity(row.site_id);
  const equipment: CachedEquipment | undefined = row.equipment_id ? await readEntity(row.equipment_id) : undefined;
  return { intervention: row, customer, site, equipment };
}

/** Une intervention gardée sur l'appareil, avec son contexte ; `undefined` si elle n'y est pas. */
export async function cachedVisit(id: string): Promise<CachedVisit | undefined> {
  const row = await readEntity<CachedInterventionRow>(id);
  return row ? context(row) : undefined;
}

/** Rapport synchronisé (`/sync/pull`), avec les modifications encore en file. */
export interface CachedReportRow {
  id: string;
  intervention_id: string;
  revision?: number;
  status?: string;
  body?: string | null;
  fault_reported?: boolean;
  fault_detail?: string | null;
  recommendations?: string | null;
  checklist_template_id?: string | null;
  checklist_results?: Record<string, unknown> | null;
  photo_before_ids?: string[];
  photo_after_ids?: string[];
  [field: string]: unknown;
}

/**
 * Le dernier rapport d'une intervention gardé sur l'appareil (WEB1-06) : la
 * révision la plus récente, avec la saisie encore en file ; `undefined` si
 * l'appareil n'en a aucun. La copie née d'une saisie hors ligne (`pending`,
 * sans révision) passe avant : elle vise un rapport plus récent que ceux du
 * dernier pull, par exemple la révision créée par une réouverture en ligne.
 */
export async function cachedReport(interventionId: string): Promise<CachedReportRow | undefined> {
  const rank = (row: CachedReportRow): number => (row.pending === true ? Number.MAX_SAFE_INTEGER : Number(row.revision ?? 0));
  const latest = (await listEntities<CachedReportRow>('report'))
    .filter((row) => row.intervention_id === interventionId)
    .sort((a, b) => rank(b) - rank(a))[0];
  return latest ? readEntity<CachedReportRow>(latest.id) : undefined;
}

/** Modèle de checklist actif gardé sur l'appareil (WEB1-12). */
export interface CachedChecklistTemplate {
  id: string;
  name: string;
  intervention_type: string | null;
  equipment_category: string | null;
  items: unknown[];
}

export async function cachedChecklistTemplates(): Promise<CachedChecklistTemplate[]> {
  return (await listEntities<CachedChecklistTemplate>('checklist_template'))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
}

/** Machine synchronisée (`/sync/pull`), jeton QR compris. */
export interface CachedEquipmentRow extends CachedEquipment {
  id: string;
  customer_id: string;
  site_id: string;
  status?: string;
  qr_token?: string | null;
  /** Saisies fluides permises (droit du rôle, module Froid ; pull) ; absent d'un ancien cache : le serveur tranche à l'envoi. */
  fluids_writable?: boolean;
  [field: string]: unknown;
}

/** Une machine gardée sur l'appareil, son client, son site et les autres machines du site. */
export interface CachedMachine {
  equipment: CachedEquipmentRow;
  customer?: CachedCustomer | undefined;
  site?: CachedSite | undefined;
  siblings: CachedEquipmentRow[];
}

export async function cachedMachine(id: string): Promise<CachedMachine | undefined> {
  const equipment = await readEntity<CachedEquipmentRow>(id);
  if (!equipment) return undefined;
  const siblings = (await listEntities<CachedEquipmentRow>('equipment'))
    .filter((other) => other.site_id === equipment.site_id && other.id !== equipment.id);
  const customer: CachedCustomer | undefined = await readEntity(equipment.customer_id);
  const site: CachedSite | undefined = await readEntity(equipment.site_id);
  return { equipment, customer, site, siblings };
}

/** Machine de l'appareil dont l'étiquette porte ce jeton (scan sans réseau). */
export async function cachedEquipmentIdByQr(token: string): Promise<string | undefined> {
  return (await listEntities<CachedEquipmentRow>('equipment')).find((row) => row.qr_token === token)?.id;
}

/** Les interventions gardées que `select` retient (filtre et ordre), avec leur contexte. */
export async function cachedVisits(
  select: (rows: CachedInterventionRow[]) => CachedInterventionRow[],
): Promise<CachedVisit[]> {
  const merged: CachedInterventionRow[] = [];
  for (const row of await listEntities<CachedInterventionRow>('intervention')) {
    const current = await readEntity<CachedInterventionRow>(row.id);
    if (current) merged.push(current);
  }
  const visits: CachedVisit[] = [];
  for (const row of select(merged)) visits.push(await context(row));
  return visits;
}
